/**
 * P4-3 — the document library, the read side of the Dropbox adapter.
 *
 * Three things this screen deliberately does NOT do:
 *
 *  - It never renders a file it fetched itself. §2.4 Flow B hands the reader a
 *    short-lived Dropbox link; the bytes never pass through the platform, so
 *    "Open" asks the server for a link at the moment it is clicked rather than
 *    holding one that would expire in the list.
 *  - It never explains an empty list as "no documents" when the real reason is
 *    that Dropbox is not connected. An unconfigured environment gets a panel
 *    that names the three variables that are missing, because that is a
 *    deployment step, not a data state.
 *  - It never draws a broken image. A format Dropbox will not thumbnail gets a
 *    category glyph, decided by the server's `thumbnailAvailable` rather than
 *    by a failed request.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
  type DocumentCategory,
  type DocumentSummary,
  type HealthReport,
  type PillarCode,
} from '../lib/api';

interface Props {
  scope: PillarCode | null;
}

type DropboxState = 'unknown' | 'unconfigured' | 'no_baseline' | 'linked' | 'check_failed';

interface DropboxCheck {
  state?: DropboxState;
  missingEnv?: string[];
  error?: string;
  missingFolderNames?: string[];
  visibilityAlert?: boolean;
}

/**
 * The one line the reader gets about the link itself. §2.3 is explicit that a
 * shrinking top-level listing is never suppressed, so an alert says which
 * folders went missing rather than that something is wrong.
 */
function linkNotice(check: DropboxCheck & { state: DropboxState }): string | null {
  if (check.state === 'no_baseline') {
    return 'Dropbox is configured, but the folder-visibility baseline has not been recorded yet. The next successful check will set it.';
  }
  if (check.state === 'check_failed') {
    return `Dropbox is configured but the last visibility check did not complete${
      check.error ? ` \u2014 ${check.error}` : ''
    }.`;
  }
  if (check.state === 'linked' && (check.missingFolderNames?.length ?? 0) > 0) {
    return `Top-level folders no longer visible since the previous check: ${check.missingFolderNames!.join(
      ', ',
    )}.`;
  }
  return null;
}

function readDropboxCheck(report: HealthReport | null): DropboxCheck & { state: DropboxState } {
  const raw = (report?.checks?.dropbox ?? {}) as DropboxCheck;
  return { ...raw, state: raw.state ?? 'unknown' };
}

/**
 * The server's CHECK constraint is the authority on what a category may be, so
 * an unrecognised value is shown as sent rather than dropped.
 */
function categoryLabel(category: string): string {
  return DOCUMENT_CATEGORY_LABELS[category as DocumentCategory] ?? category;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/* -------------------------------------------------------------- glyphs -- */

/**
 * One glyph per document family, drawn rather than imported: the file types
 * that matter here are a construction set, not a generic office set.
 */
function CategoryGlyph({ category }: { category: string }): JSX.Element {
  const paths: Record<string, JSX.Element> = {
    contract: (
      <>
        <path d="M7 3h7l5 5v13H7Z" />
        <path d="M14 3v5h5" />
        <path d="M10 13h7M10 17h4" />
      </>
    ),
    estimate: (
      <>
        <rect x="6" y="3" width="12" height="18" rx="2" />
        <path d="M9 7h6M9 11h2M13 11h2M9 15h2M13 15h2" />
      </>
    ),
    invoice: (
      <>
        <path d="M6 3h12v18l-3-2-3 2-3-2-3 2Z" />
        <path d="M9 8h6M9 12h6" />
      </>
    ),
    photo: (
      <>
        <rect x="3" y="6" width="18" height="13" rx="2" />
        <circle cx="9" cy="11" r="2" />
        <path d="m4 18 5-4 4 3 3-2 4 3" />
      </>
    ),
    permit: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <circle cx="12" cy="10" r="2.5" />
        <path d="M8 17c1-2 2.5-3 4-3s3 1 4 3" />
      </>
    ),
    coi: (
      <>
        <path d="M12 3 5 6v5c0 4 3 7.5 7 9 4-1.5 7-5 7-9V6Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-7 w-7"
    >
      {paths[category] ?? (
        <>
          <path d="M7 3h7l5 5v13H7Z" />
          <path d="M14 3v5h5" />
        </>
      )}
    </svg>
  );
}

/* ---------------------------------------------------------- thumbnail -- */

/**
 * The thumbnail is fetched once per card and released on unmount. An object URL
 * that is never revoked keeps the decoded image alive for the life of the tab,
 * which on a photo-heavy project is tens of megabytes of retained memory.
 */
function Thumbnail({ doc }: { doc: DocumentSummary }): JSX.Element {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    if (!doc.thumbnailAvailable) return;
    void (async () => {
      try {
        const blob = await api.documentThumbnail(doc.id);
        if (!live) return;
        if (!blob) {
          setFailed(true);
          return;
        }
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setSrc(url);
      } catch {
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [doc.id, doc.thumbnailAvailable]);

  const showGlyph = !doc.thumbnailAvailable || failed;

  return (
    <div className="flex h-14 w-14 flex-none items-center justify-center overflow-hidden rounded-card border border-border bg-surface text-text-secondary">
      {showGlyph ? (
        <CategoryGlyph category={doc.category} />
      ) : src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full animate-pulse bg-border/40" />
      )}
    </div>
  );
}

/* ------------------------------------------------- unconfigured panel -- */

function NotConnected({ missing }: { missing: string[] }): JSX.Element {
  return (
    <div className="rounded-card border border-border bg-white p-5">
      <h2 className="text-h3 font-semibold text-text-primary">Dropbox is not connected</h2>
      <p className="mt-1 max-w-prose text-body text-text-secondary">
        The adapter is deployed and the library is ready. It needs the app credentials before it
        can read or write anything, and it will not report a link it has not tested.
      </p>
      <p className="mt-4 text-micro font-medium uppercase tracking-wide text-text-secondary">
        Set in the service environment
      </p>
      <ul className="mt-1.5 space-y-1">
        {(missing.length > 0
          ? missing
          : ['DROPBOX_APP_KEY', 'DROPBOX_APP_SECRET', 'DROPBOX_REFRESH_TOKEN']
        ).map((name) => (
          <li key={name} className="numeric text-body text-text-primary">
            {name}
          </li>
        ))}
      </ul>
      <p className="mt-4 max-w-prose text-micro text-text-secondary">
        The account is an individual Professional account with no team space, so no path-root
        namespace is set and none is required.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- screen -- */

export function DocumentLibrary({ scope }: Props): JSX.Element {
  const [health, setHealth] = useState<HealthReport | null>(null);
  const [feed, setFeed] = useState<DocumentSummary[] | null>(null);
  const [category, setCategory] = useState<string>('');
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const next = await api.health();
        if (live) setHealth(next);
      } catch {
        if (live) setHealth(null);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    setFeed(null);
    void (async () => {
      try {
        const next = await api.documents(scope, category || null);
        if (live) setFeed(next.documents);
      } catch {
        if (live) setFeed([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [scope, category]);

  const dropbox = useMemo(() => readDropboxCheck(health), [health]);
  const notice = useMemo(() => linkNotice(dropbox), [dropbox]);

  /**
   * §2.4 Flow B: the link is requested on the click, then opened. A link
   * fetched with the list would be four hours older than the click.
   */
  async function open(doc: DocumentSummary): Promise<void> {
    setOpening(doc.id);
    setError(null);
    try {
      const { link } = await api.documentLink(doc.id);
      window.open(link, '_blank', 'noopener,noreferrer');
    } catch {
      setError(`Could not get a link for "${doc.title}". The file may have moved in Dropbox.`);
    } finally {
      setOpening(null);
    }
  }

  return (
    <>
      <h1 className="mb-1 text-h1 font-semibold text-text-primary">Documents</h1>
      <p className="mb-4 text-body text-text-secondary">
        Filed in Dropbox under each project&rsquo;s folder, newest first
        {scope ? ` \u00b7 ${scope}` : ''}
      </p>

      {dropbox.state === 'unconfigured' ? (
        <NotConnected missing={dropbox.missingEnv ?? []} />
      ) : (
        <>
          {notice ? (
            <p role="status" className="mb-3 flex items-start gap-2 text-body text-warning">
              <span
                aria-hidden="true"
                className="mt-1.5 block h-2 w-2 flex-none rounded-full bg-warning"
              />
              <span>{notice}</span>
            </p>
          ) : null}

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label htmlFor="doc-category" className="text-body text-text-secondary">
              Category
            </label>
            <select
              id="doc-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-card border border-border bg-white px-3 py-1.5 text-body text-text-primary"
            >
              <option value="">All categories</option>
              {DOCUMENT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {DOCUMENT_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            {feed !== null ? (
              <span className="text-body text-text-secondary">
                <span className="numeric">{feed.length}</span>{' '}
                {feed.length === 1 ? 'document' : 'documents'}
              </span>
            ) : null}
          </div>

          {error ? (
            <p role="alert" className="mb-3 text-body text-danger">
              {error}
            </p>
          ) : null}

          {feed === null ? (
            <div className="space-y-2" aria-hidden="true">
              <div className="h-20 animate-pulse rounded-card bg-white" />
              <div className="h-20 animate-pulse rounded-card bg-white" />
              <div className="h-20 animate-pulse rounded-card bg-white" />
            </div>
          ) : feed.length === 0 ? (
            <div className="empty-state">
              {/* A filtered-out list is not an empty library, and saying so
                  would send the reader looking for a problem that is a
                  dropdown. */}
              <p className="text-body font-medium text-text-primary">
                {category
                  ? `No ${categoryLabel(category).toLowerCase()} documents`
                  : 'No documents filed yet'}
              </p>
              <p className="mt-1">
                {category
                  ? 'Nothing is filed under this category for the current entity. Clear the filter to see everything else.'
                  : 'Files uploaded against a project are written into that project\u2019s Dropbox folder and listed here. Folders excluded from the register are never listed, and nothing already in Dropbox is moved or restructured.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {feed.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-start gap-3 rounded-card border border-border bg-white p-3"
                >
                  <Thumbnail doc={doc} />
                  <div className="min-w-0 flex-1">
                    {/* The title identifies the file, so it wraps rather than
                        truncating at the first narrow breakpoint. */}
                    <p className="line-clamp-2 text-body font-medium text-text-primary">
                      {doc.title}
                    </p>
                    <p className="mt-0.5 text-micro text-text-secondary">
                      {categoryLabel(doc.category)}
                      {doc.pillar ? ` \u00b7 ${doc.pillar}` : ' \u00b7 Unfiled'}
                      {` \u00b7 ${formatBytes(doc.sizeBytes)} \u00b7 ${formatDate(doc.uploadedAt)}`}
                    </p>
                    {doc.description ? (
                      <p className="mt-1 line-clamp-2 text-micro text-text-secondary">
                        {doc.description}
                      </p>
                    ) : null}
                    {doc.tags.length > 0 ? (
                      <p className="mt-1 flex flex-wrap gap-1">
                        {doc.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-border px-2 py-0.5 text-micro text-text-secondary"
                          >
                            {tag}
                          </span>
                        ))}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => void open(doc)}
                    disabled={opening === doc.id}
                    className="btn-secondary flex-none"
                  >
                    {opening === doc.id ? 'Opening\u2026' : 'Open'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}
